import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Grid,
  Typography,
  Popover,
  Button,
  TextField,
  Checkbox,
  FormControlLabel
} from '@mui/material';
 
import SettingsIcon from '@mui/icons-material/Settings';

import { Formik, Form } from 'formik';
import DeleteField from '../../otherComponent/DeleteStep';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';

interface EsignComponentProps {
  label?: string;
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  selectedNodeData?: any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

function EsignComponent({ values, index, setFieldValue, selectedNodeData, onDeleteField, toSentenceCase, fieldTypeSelector, isSettingsOpen, onSettingsOpen, onSettingsClose }: EsignComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-esign-popover' : undefined;

  const field = values.fields[index];
  const label = field.question || 'E-Signature';

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={0.5}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 'calc(100% - 40px)',
                py: 1,
                px: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: 0
              }}
            >
              <Typography
                variant="body1"
                sx={{ flexGrow: 1, mr: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {toSentenceCase(label)}
              </Typography>
              <DrawOutlinedIcon fontSize="small" sx={{ color: 'gray', flexShrink: 0 }} />
             
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box ref={settingsButtonRef} sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleOpen}>
                <SettingsIcon fontSize="medium" />
              </Box>
              <DeleteField 
                onDeleteField={() => onDeleteField?.(index)}
                selectedNodeData={selectedNodeData}
                fieldIndex={index}
                fieldData={values.fields[index]}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Formik
          initialValues={{
            label: field.question || '',
            required: field.settings?.required || false,
            isSupportFileRequired: field.settings?.isSupportFileRequired || false,
            noOfSupportImages: field.settings?.noOfSupportImages || ''
          }}
          enableReinitialize
          onSubmit={(formValues) => {
            const updatedField = {
              ...field,
              question: formValues.label,
              settings: {
                ...field.settings,
                required: formValues.required,
                isSupportFileRequired: formValues.isSupportFileRequired,
                noOfSupportImages: formValues.isSupportFileRequired
                  ? formValues.noOfSupportImages
                  : ''
              }
            };
            setFieldValue(`fields.${index}`, updatedField);
            handleClose();
          }}
        >
          {({ values: formValues, handleChange }) => (
            <Form>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} gap={1}>
                <Typography variant="subtitle1">Field Settings</Typography>
                {fieldTypeSelector ? (
                  <Box sx={{ minWidth: 120 }}>
                    {fieldTypeSelector}
                  </Box>
                ) : null}
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Label"
                    name="label"
                    value={formValues.label}
                    onChange={handleChange}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="required"
                        checked={formValues.required}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Required"
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: -3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isSupportFileRequired"
                        checked={formValues.isSupportFileRequired || false}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Support File Required"
                  />
                </Grid>

                {formValues.isSupportFileRequired && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="noOfSupportImages"
                      type="number"
                      label="Number of Images"
                      value={formValues.noOfSupportImages || ''}
                      onChange={handleChange}
                      size="small"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button type="submit" size="small" variant="contained">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Popover>
    </Box>
  );
}

export default EsignComponent;

